import {
  Calendar,
  CloudSun,
  Heart,
  MapPin,
  Megaphone,
  Mic2,
  Music,
  Radio,
  Star,
  SunMedium,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { apiFetch } from "@/lib/api";

const iconMap: Record<string, LucideIcon> = {
  Radio,
  Mic2,
  SunMedium,
  CloudSun,
  Megaphone,
  Music,
  Heart,
  Star,
  Zap,
  MapPin,
  Calendar,
};

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.099 4.388 23.093 10.125 24v-8.437H7.078v-3.49h3.047V9.36c0-3.017 1.791-4.672 4.533-4.672 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.093 24 18.099 24 12.073z" />
    </svg>
  );
}

interface IntroBox {
  icon?: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  linkUrl?: string;
  linkText?: string;
  linkTarget?: "_self" | "_blank";
  style?: "gradient" | "white" | "outline";
}

interface TalentSchedule {
  talentId?: string;
  talentName?: string;
  stageName?: string;
  headshotUrl?: string;
  showName?: string;
}

function BoxIcon({ name }: { name?: string }) {
  const Icon = name && iconMap[name] ? iconMap[name] : SunMedium;
  return <Icon className="mb-3 h-6 w-6" />;
}

function IntroBoxCard({ box }: { box: IntroBox }) {
  const style = box.style || "white";
  const linkTarget = box.linkTarget || "_self";
  const linkRel = linkTarget === "_blank" ? "noopener noreferrer" : undefined;
  const media = box.imageUrl ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={box.imageUrl} alt="" className="mb-4 aspect-video w-full rounded-xl object-cover" loading="lazy" />
  ) : (
    <BoxIcon name={box.icon} />
  );
  const title = box.title ? <h3 className="mb-1 font-display text-lg font-bold">{box.title}</h3> : null;
  const body = (
    <div
      className="prose prose-sm max-w-none text-sm leading-relaxed"
      dangerouslySetInnerHTML={{ __html: box.description || "" }}
    />
  );
  const link = box.linkUrl && box.linkText && (
    <a
      href={box.linkUrl}
      target={linkTarget}
      rel={linkRel}
      className="mt-3 inline-flex items-center text-sm font-semibold text-[rgb(var(--color-primary))] hover:underline"
    >
      {box.linkText}
    </a>
  );

  if (style === "gradient") {
    return (
      <div className="rounded-3xl bg-gradient-to-br from-[rgb(var(--color-primary))] to-[#f15bb5] p-5 text-white shadow-lg">
        {box.imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={box.imageUrl} alt="" className="mb-4 aspect-video w-full rounded-xl object-cover" loading="lazy" />
        ) : (
          <BoxIcon name={box.icon} />
        )}
        {box.title && <h3 className="mb-1 font-display text-lg font-bold text-white">{box.title}</h3>}
        <div
          className="prose prose-sm max-w-none text-sm font-semibold leading-relaxed"
          dangerouslySetInnerHTML={{ __html: box.description || "" }}
        />
        {box.linkUrl && box.linkText && (
          <a
            href={box.linkUrl}
            target={linkTarget}
            rel={linkRel}
            className="mt-3 inline-flex items-center text-sm font-bold text-white hover:underline"
          >
            {box.linkText}
          </a>
        )}
      </div>
    );
  }

  if (style === "outline") {
    return (
      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-[rgb(var(--color-primary))] shadow-sm">
        {media}
        {title}
        {body}
        {link}
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-[var(--color-text)] shadow-sm">
      {media}
      {title}
      {body}
      {link}
    </div>
  );
}

async function fetchTalentSchedule(): Promise<TalentSchedule[]> {
  try {
    return await apiFetch<TalentSchedule[]>("/v1/talent/schedule");
  } catch {
    return [];
  }
}

export async function SunnyIntroBlock({
  content,
  template = "sunny",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const badgeText = String(content.badgeText ?? "Now on 106.5 FM");
  const heading = String(content.heading ?? "We’re on the air at 106.5 FM");
  const body = Array.isArray(content.body) ? (content.body as string[]) : [];
  const boxes = Array.isArray(content.boxes) ? (content.boxes as IntroBox[]) : [];
  const showOnAirTalent = content.showOnAirTalent !== false;
  const showFacebookWidget = content.showFacebookWidget !== false;
  const facebookPageUrl = String(content.facebookPageUrl ?? "https://www.facebook.com/1065Sunny/");
  const encodedFacebookUrl = encodeURIComponent(facebookPageUrl);

  const schedule = showOnAirTalent ? await fetchTalentSchedule() : [];
  const featuredTalent = schedule.slice(0, 3).map((item) => ({
    key: item.talentId || item.stageName || item.talentName,
    name: item.stageName || item.talentName || "Sunny 106.5",
    showName: item.showName || null,
    headshotUrl: item.headshotUrl || null,
  }));

  return (
    <section
      data-template={template}
      className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-[var(--color-surface)] to-cyan-50 py-12 md:py-16"
    >
      <div className="container relative mx-auto px-4">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1fr_360px] lg:items-start">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-bold uppercase tracking-[0.18em] text-amber-800">
              <Radio className="h-4 w-4" />
              {badgeText}
            </div>
            <div>
              <h2
                className="font-bold leading-tight text-[var(--color-text)] md:text-4xl"
                style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h1)", lineHeight: "var(--line-heading)" }}
              >
                {heading}
              </h2>
              {body.length > 0 && (
                <div className="mt-4 space-y-4 text-lg leading-relaxed text-[var(--color-text)]/80">
                  {body.map((paragraph, index) => (
                    <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              )}
            </div>

            {boxes.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-3">
                {boxes.slice(0, 3).map((box, index) => (
                  <IntroBoxCard key={index} box={box} />
                ))}
              </div>
            )}

            {showOnAirTalent && featuredTalent.length > 0 && (
              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[var(--color-text)]">
                  <Mic2 className="h-4 w-4 text-[rgb(var(--color-primary))]" />
                  On-air talent
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {featuredTalent.map((person) => (
                    <div key={person.key} className="flex items-center gap-3 rounded-xl bg-[var(--color-surface-muted)] p-3">
                      {person.headshotUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={person.headshotUrl} alt="" className="h-12 w-12 rounded-full object-cover" loading="lazy" />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]">
                          <Mic2 className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[var(--color-text)]">{person.name}</p>
                        {person.showName && <p className="truncate text-xs text-[var(--color-text)]/70">{person.showName}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {showFacebookWidget && (
            <aside className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-lg">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[var(--color-text)]">
                <FacebookIcon className="h-4 w-4 text-[#1877F2]" />
                Sunny on Facebook
              </div>
              <iframe
                title="Sunny 106.5 Facebook feed"
                src={`https://www.facebook.com/plugins/page.php?href=${encodedFacebookUrl}&tabs=timeline&width=340&height=420&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`}
                className="block min-h-[50vh] w-full overflow-hidden border-0 md:min-h-[420px]"
                scrolling="no"
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              />
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}
